// What? A React component for managing document structure (sections) in document type creation/editing.
// Why? To allow users to define the hierarchical structure of generated documents.
// How? The component's logic flow:
// 1. Template Upload: Parse Word templates to auto-generate sections
// 2. Add/Remove: Manually add or remove document sections (removal is
//    confirmed first, since it also deletes nested sections and prompts)
// 3. Reorder: Drag-and-drop sections to change order
// 4. Configure: Set section title, prompt, parent relationship, and dynamic flag
// 5. Hierarchy: Support parent-child relationships with max 5 levels depth
// 6. Persist: Auto-save changes to backend as user edits

import React, {
  useCallback,
  useRef,
  useState,
  useMemo,
  useEffect,
} from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  ChipDelete,
  DialogTitle,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  List,
  ListItem,
  ListItemContent,
  ListItemDecorator,
  Modal,
  ModalDialog,
  Option,
  Select,
  Stack,
  Textarea,
  Tooltip,
  Typography,
} from "@mui/joy";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
  DraggableProvided,
  DraggableStateSnapshot,
} from "@hello-pangea/dnd";
import DeleteIcon from "@mui/icons-material/Delete";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DescriptionIcon from "@mui/icons-material/Description";
import { useDropzone } from "react-dropzone";

import {
  DocumentTypeSection,
  DocumentTypeInput,
  SourceDocumentDefinition,
} from "../../../types/documentTypes";
import {
  useDeleteDocumentTypeSection,
  useCreateDocumentTypeSection,
  useUpdateDocumentTypeSection,
  useDocumentTypeSections,
} from "../../../hooks/useDocumentTypeSections";
import { useValidation } from "../../../contexts/ValidationContext";
import { useFeatureFlag } from "../../../../../providers/FeatureFlagProvider";
import { useAuthHeaders } from "../../../../../hooks/useAuthHeaders";
import { UPPERBAR_HEIGHT_PX } from "../../../../../components/Upperbar";
import { FloatingChatWidget } from "../MeridianStructureAgent";
import {
  StructureNavigator,
  buildTreeFromDocumentTypeSections,
} from "../../common/StructureNavigator";
import AddSectionButton from "../../common/AddSectionButton";
import { getInsertIndexAfterSubtree } from "./sectionInsertPosition";
import { getApiBaseUrl } from "../../../../../config/runtimeConfig";

// --- Define type for the uploaded refinement file data ---
// Export this interface so it can be imported elsewhere
export interface UploadedRefinementData {
  fileId: string; // Relative path ID
  fileName: string; // Original filename for display
  parsedContent: string; // The parsed text content
}

// Define props for the helper component
interface SectionRefinementProps {
  section: DocumentTypeSection;
  documentTypeId?: number;
  sections: DocumentTypeSection[]; // Receive the full sections array
  uploadedRefinementData: UploadedRefinementData | null;
  setUploadedRefinementData: (data: UploadedRefinementData | null) => void;
  selectedGeneratedDocId: number | "";
  setSelectedGeneratedDocId: (id: number | "") => void;
}

// Define SectionRefinement as a standalone component
const SectionRefinement: React.FC<SectionRefinementProps> = ({
  section,
  documentTypeId,
  sections, // Use sections prop
  uploadedRefinementData,
  setUploadedRefinementData,
  selectedGeneratedDocId,
  setSelectedGeneratedDocId,
}) => {
  // Empty component - refinement functionality commented out for now

  if (!documentTypeId) return null;

  return (
    <>
      {/* COMMENTED OUT FOR NOW AS WE ARE NOT USING IT
      <Tooltip title="Refine prompt for this section">
        <Button
          variant="outlined"
          size="sm"
          onClick={() => setIsRefineModalOpen(true)}
          startDecorator={<RefreshIcon />}
        >
          Refine Prompt
        </Button>
      </Tooltip> */}

      {/* {isRefineModalOpen && (
        <PromptRefinementModal
          isOpen={isRefineModalOpen}
          onClose={() => setIsRefineModalOpen(false)}
          documentTypeId={documentTypeId}
          sectionId={section.DocumentTypeSectionID}
          onSuccess={handleRefinementSuccess}
          uploadedRefinementData={uploadedRefinementData}
          setUploadedRefinementData={setUploadedRefinementData}
          selectedGeneratedDocId={selectedGeneratedDocId}
          setSelectedGeneratedDocId={setSelectedGeneratedDocId}
        />
      )} */}
    </>
  );
};

// --- SectionItem Component (Memoized) ---
interface SectionItemProps {
  section: DocumentTypeSection;
  index: number;
  // Stable DOM id used by the StructureNavigator to scroll to this card.
  sectionIdKey: string;
  provided: DraggableProvided; // From react-beautiful-dnd
  snapshot: DraggableStateSnapshot;
  updateSection: (index: number, updates: Partial<DocumentTypeSection>) => void;
  // Opens the delete confirmation modal - deletion happens only after confirm.
  requestRemoveSection: (index: number) => void;
  getIndentLevel: (section: DocumentTypeSection) => number;
  documentTypeId?: number;
  sections: DocumentTypeSection[]; // For SectionRefinement
  updatePrompt?: (index: number, promptText: string) => void; // For SectionRefinement
  availableParents: DocumentTypeSection[];
  updateSectionParent: (index: number, parentId: number | null) => void;
  uploadedRefinementData: UploadedRefinementData | null;
  setUploadedRefinementData: (data: UploadedRefinementData | null) => void;
  selectedGeneratedDocId: number | "";
  setSelectedGeneratedDocId: (id: number | "") => void;
  persistTitle: (index: number) => void;
  persistPrompt: (index: number) => void;
  onTitleFocus: (index: number, value: string) => void;
  onPromptFocus: (index: number, value: string) => void;
  onDynamicChange: (index: number, checked: boolean) => void;
  onSectionTypeChange: (
    index: number,
    sectionType: "Standard" | "Thinking",
  ) => void;
  onSourcesChange: (index: number, sourceFieldIds: number[]) => void;
  availableSources: SourceDocumentDefinition[];
  isSectionSourceSelectionEnabled: boolean;
  isThinkingEnabled: boolean;
  promptLoadingState?: "generating" | "complete" | "error";
  promptError?: string;
  // Inserts a new sibling section directly below this one.
  onAddSectionBelow: (index: number) => void;
  // True while this section's own "add below" request is in flight.
  isAddingHere: boolean;
  // True while any add request is in flight, so we don't queue up several.
  isAddPending: boolean;
}

const SectionItem = React.memo<SectionItemProps & { showValidation: boolean }>(
  ({
    section,
    index,
    sectionIdKey,
    provided,
    snapshot,
    updateSection,
    requestRemoveSection,
    getIndentLevel,
    documentTypeId,
    sections,
    updatePrompt,
    availableParents,
    updateSectionParent,
    uploadedRefinementData,
    setUploadedRefinementData,
    selectedGeneratedDocId,
    setSelectedGeneratedDocId,
    persistTitle,
    persistPrompt,
    onTitleFocus,
    onPromptFocus,
    onDynamicChange,
    onSectionTypeChange,
    onSourcesChange,
    availableSources,
    isSectionSourceSelectionEnabled,
    isThinkingEnabled,
    showValidation,
    promptLoadingState,
    promptError,
    onAddSectionBelow,
    isAddingHere,
    isAddPending,
  }) => {
    // Determine border color based on prompt loading state
    const getBorderColor = () => {
      if (promptLoadingState === "generating") return "primary.300";
      if (promptLoadingState === "error") return "danger.300";
      return "neutral.300";
    };

    return (
      <Box
        ref={provided.innerRef}
        {...provided.draggableProps}
        // Scroll anchor for the StructureNavigator. The root ref belongs to
        // dnd, so the navigator addresses this card by attribute instead.
        data-nav-section-id={sectionIdKey}
        sx={{
          ...provided.draggableProps.style,
          p: 2,
          mb: 2,
          ml: `${getIndentLevel(section) * 2}rem`,
          border: "1px solid",
          borderColor: getBorderColor(),
          borderRadius: "sm",
          transition: "all 0.2s ease",
          backgroundColor:
            promptLoadingState === "generating" ? "primary.50" : "transparent",
          "&:hover": {
            borderColor:
              promptLoadingState === "generating"
                ? "primary.400"
                : "neutral.400",
            boxShadow: "sm",
          },
        }}
      >
        <Stack spacing={2}>
          {/* Header row with drag handle and actions */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ flexWrap: "wrap" }}
          >
            <Box
              {...provided.dragHandleProps}
              sx={{
                cursor: "grab",
                px: 1,
                color: "text.secondary",
                "&:active": { cursor: "grabbing" },
              }}
            >
              ⋮⋮
            </Box>

            <FormControl sx={{ flex: 1, minWidth: { xs: "100%", sm: 200 } }}>
              <FormLabel>Section Title</FormLabel>
              <Input
                value={section.Title}
                onChange={(e) =>
                  updateSection(index, { Title: e.target.value })
                }
                onFocus={() => onTitleFocus(index, section.Title || "")}
                onBlur={() => persistTitle(index)}
                placeholder="Enter section title"
                size="lg"
                slotProps={{
                  input: {
                    maxLength: 100,
                    // Lets the StructureNavigator focus this field after
                    // scrolling here, so you can edit straight away.
                    "data-section-title-input": "true",
                  },
                }}
              />
            </FormControl>

            <FormControl sx={{ minWidth: { xs: "100%", sm: 200 } }}>
              <FormLabel>Parent Section</FormLabel>
              <Select
                value={section.ParentSectionID?.toString() || ""}
                onChange={(_, newValue) =>
                  updateSectionParent(
                    index,
                    newValue ? parseInt(newValue) : null,
                  )
                }
                size="lg"
              >
                <Option value="">No Parent</Option>
                {availableParents
                  .filter(
                    (
                      p,
                    ): p is DocumentTypeSection & {
                      DocumentTypeSectionID: number;
                    } => p.DocumentTypeSectionID !== null,
                  )
                  .map((parent) => (
                    <Option
                      key={parent.DocumentTypeSectionID}
                      value={String(parent.DocumentTypeSectionID)}
                    >
                      {parent.Title}
                    </Option>
                  ))}
              </Select>
            </FormControl>

            <Stack direction="row" spacing={2} alignItems="flex-start">
              <Stack spacing={0.5}>
                <Checkbox
                  checked={section.Dynamic || false}
                  onChange={(e) => onDynamicChange(index, e.target.checked)}
                  label="Dynamic"
                />
                {isThinkingEnabled && (
                  <Tooltip
                    title="This section performs multi-step AI research and shows its reasoning process. Generation takes longer but produces more thorough results."
                    placement="right"
                    arrow
                  >
                    <Checkbox
                      checked={section.SectionType === "Thinking"}
                      onChange={(e) =>
                        onSectionTypeChange(
                          index,
                          e.target.checked ? "Thinking" : "Standard",
                        )
                      }
                      label="Thinking"
                    />
                  </Tooltip>
                )}
              </Stack>

              <Tooltip title="Remove Section">
                <IconButton
                  onClick={() => requestRemoveSection(index)}
                  color="danger"
                  variant="outlined"
                  size="sm"
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>

              <SectionRefinement
                section={section}
                documentTypeId={documentTypeId}
                sections={sections}
                uploadedRefinementData={uploadedRefinementData}
                setUploadedRefinementData={setUploadedRefinementData}
                selectedGeneratedDocId={selectedGeneratedDocId}
                setSelectedGeneratedDocId={setSelectedGeneratedDocId}
              />
            </Stack>
          </Stack>
          <FormControl>
            <Stack direction="row" alignItems="center" spacing={1}>
              <FormLabel sx={{ mb: 0 }}>Prompt (optional)</FormLabel>
              {promptLoadingState === "generating" && (
                <Chip size="sm" variant="soft" color="primary">
                  Generating...
                </Chip>
              )}
              {promptLoadingState === "error" && (
                <Chip size="sm" variant="soft" color="danger">
                  Failed
                </Chip>
              )}
            </Stack>
            <Textarea
              value={section.prompts?.[0]?.PromptText || ""}
              onChange={(e) =>
                updateSection(index, {
                  prompts: [
                    {
                      Name: "Default",
                      PromptText: e.target.value,
                      IsDefault: true,
                    },
                  ],
                })
              }
              onFocus={() =>
                onPromptFocus(index, section.prompts?.[0]?.PromptText || "")
              }
              onBlur={() => persistPrompt(index)}
              placeholder={
                promptLoadingState === "generating"
                  ? "Generating prompt..."
                  : "Enter the prompt for this section (optional)..."
              }
              size="lg"
              minRows={3}
              slotProps={{ textarea: { maxLength: 10000 } }}
              sx={{ resize: "vertical" }}
              disabled={promptLoadingState === "generating"}
            />
            {promptError && (
              <Typography level="body-xs" color="danger" sx={{ mt: 0.5 }}>
                {promptError}
              </Typography>
            )}
          </FormControl>

          {(() => {
            if (!isSectionSourceSelectionEnabled) return null;
            const selectableSources = availableSources.filter(
              (s): s is SourceDocumentDefinition & { SourceFieldID: number } =>
                s.DocumentType === "Source" &&
                typeof s.SourceFieldID === "number",
            );
            if (selectableSources.length === 0) return null;
            const selected = section.AllowedSourceFieldIDs ?? [];
            return (
              <FormControl>
                <FormLabel sx={{ mb: 0 }}>Source documents</FormLabel>
                <Typography
                  level="body-xs"
                  sx={{ mb: 0.5, color: "text.secondary" }}
                >
                  Source documents this section refers to — leave empty for all
                  (default).
                </Typography>
                <Select
                  multiple
                  value={selected}
                  onChange={(_, newValue) =>
                    onSourcesChange(index, (newValue as number[]) ?? [])
                  }
                  placeholder="All sources"
                  size="lg"
                  renderValue={(selectedOptions) =>
                    selectedOptions.length === 0 ? (
                      <Typography
                        level="body-sm"
                        sx={{ color: "text.tertiary" }}
                      >
                        All sources
                      </Typography>
                    ) : (
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 0.5,
                        }}
                      >
                        {selectedOptions.map((o) => (
                          <Chip
                            key={o.value}
                            size="sm"
                            variant="soft"
                            color="primary"
                            endDecorator={
                              <ChipDelete
                                aria-label={`Remove ${o.label}`}
                                // Stop the Select from opening when the
                                // chip's "x" is pressed.
                                onMouseDown={(e) => {
                                  e.stopPropagation();
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onSourcesChange(
                                    index,
                                    selected.filter((id) => id !== o.value),
                                  );
                                }}
                              />
                            }
                          >
                            {o.label}
                          </Chip>
                        ))}
                      </Box>
                    )
                  }
                  slotProps={{
                    listbox: { sx: { zIndex: 9999 } },
                  }}
                >
                  {selected.length > 0 && (
                    <Button
                      variant="plain"
                      color="neutral"
                      size="sm"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onSourcesChange(index, []);
                      }}
                      sx={{
                        justifyContent: "flex-start",
                        width: "100%",
                        mb: 0.5,
                        fontWeight: 600,
                      }}
                    >
                      ↺ Reset to all sources
                    </Button>
                  )}
                  {selectableSources.map((s) => {
                    const isChecked = selected.includes(s.SourceFieldID);
                    return (
                      <Option key={s.SourceFieldID} value={s.SourceFieldID}>
                        <ListItemContent>{s.Label || s.Name}</ListItemContent>
                        <ListItemDecorator sx={{ ml: "auto" }}>
                          <Checkbox
                            checked={isChecked}
                            readOnly
                            tabIndex={-1}
                            size="sm"
                            sx={{ pointerEvents: "none" }}
                          />
                        </ListItemDecorator>
                      </Option>
                    );
                  })}
                </Select>
              </FormControl>
            );
          })()}

          {/* Precision add: inserts a sibling directly below this section. */}
          <Stack direction="row" justifyContent="flex-end">
            <AddSectionButton
              onClick={() => onAddSectionBelow(index)}
              label="Add Section Below"
              ariaLabel={`Add a section below ${section.Title || "this section"}`}
              tooltip="Insert a new section directly below this one, at the same level"
              variant="plain"
              color="neutral"
              loading={isAddingHere}
              disabled={isAddPending && !isAddingHere}
            />
          </Stack>
        </Stack>
      </Box>
    );
  },
);

// Types for SSE streaming events
interface SSEStructureCompleteEvent {
  sections: DocumentTypeSection[];
}

interface SSEPromptCompleteEvent {
  section_id: number;
  order_index: number;
  prompt_text: string;
}

interface SSEPromptErrorEvent {
  section_id: number;
  order_index: number;
  error: string;
}

// SSEDoneEvent is parsed but type not directly used (data is accessed as generic object)
// interface SSEDoneEvent { total_sections: number; }

// Analysis status tracking
type AnalysisPhase =
  "idle" | "uploading" | "extracting" | "generating" | "done" | "error";

// JoyUI-based File Uploader Component with SSE streaming support
interface JoyFileUploaderProps {
  documentTypeId?: number;
  workspace_id: string;
  onStructureComplete: (sections: DocumentTypeSection[]) => void;
  onPromptComplete: (sectionId: number, promptText: string) => void;
  onPromptError: (sectionId: number, error: string) => void;
  onAnalysisComplete: () => void;
  onUploadError: (error: string) => void;
  requireConfirmation?: boolean;
  confirmationMessage?: string;
  confirmationTitle?: string;
}

const JoyFileUploader: React.FC<JoyFileUploaderProps> = ({
  documentTypeId,
  workspace_id,
  onStructureComplete,
  onPromptComplete,
  onPromptError,
  onAnalysisComplete,
  onUploadError,
  requireConfirmation = false,
  confirmationMessage = "This will delete all existing sections and their prompts. Are you sure you want to continue?",
  confirmationTitle = "Confirm Template Analysis",
}) => {
  const [files, setFiles] = useState<File[]>([]);
  const [analysisPhase, setAnalysisPhase] = useState<AnalysisPhase>("idle");
  const [progress, setProgress] = useState({ completed: 0, total: 0 });
  const [error, setError] = useState<string | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pendingUpload, setPendingUpload] = useState<File[]>([]);

  const getAuthHeaders = useAuthHeaders();

  const uploadFileWithStreaming = useCallback(
    async (file: File) => {
      setAnalysisPhase("uploading");
      setError(null);

      try {
        const formData = new FormData();
        formData.append("file", file);

        // Get auth headers using the proper auth method (Auth0/EntraID)
        const authHeaders = await getAuthHeaders();
        const apiUrl = getApiBaseUrl();

        const response = await fetch(
          `${apiUrl}/api/workspace/meridian/admin/document_types/${documentTypeId}/analyze_template/stream?workspace_id=${workspace_id}`,
          {
            method: "POST",
            body: formData,
            headers: authHeaders,
          },
        );

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(errorText || `HTTP ${response.status}`);
        }

        if (!response.body) {
          throw new Error("Response body is null");
        }

        setAnalysisPhase("extracting");

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        let eventType = "";
        let eventData = "";

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });

          // Parse SSE events from buffer
          const lines = buffer.split("\n");
          buffer = lines.pop() || ""; // Keep incomplete line in buffer

          for (const line of lines) {
            if (line.startsWith("event: ")) {
              eventType = line.slice(7).trim();
            } else if (line.startsWith("data: ")) {
              eventData = line.slice(6);
            } else if (line === "" && eventType && eventData) {
              // Process complete event
              try {
                const data = JSON.parse(eventData);

                switch (eventType) {
                  case "structure_complete": {
                    const evt = data as SSEStructureCompleteEvent;
                    setAnalysisPhase("generating");
                    setProgress({ completed: 0, total: evt.sections.length });
                    onStructureComplete(evt.sections);
                    break;
                  }
                  case "import_complete": {
                    // Import has sections with prompts already - skip Phase 2
                    const evt = data as SSEStructureCompleteEvent;
                    setAnalysisPhase("done");
                    onStructureComplete(evt.sections);
                    onAnalysisComplete();
                    break;
                  }
                  case "prompt_complete": {
                    const evt = data as SSEPromptCompleteEvent;
                    setProgress((prev) => ({
                      ...prev,
                      completed: prev.completed + 1,
                    }));
                    onPromptComplete(evt.section_id, evt.prompt_text);
                    break;
                  }
                  case "prompt_error": {
                    const evt = data as SSEPromptErrorEvent;
                    setProgress((prev) => ({
                      ...prev,
                      completed: prev.completed + 1,
                    }));
                    onPromptError(evt.section_id, evt.error);
                    break;
                  }
                  case "done": {
                    setAnalysisPhase("done");
                    onAnalysisComplete();
                    break;
                  }
                  case "error": {
                    setAnalysisPhase("error");
                    setError(data.error || "Unknown error");
                    onUploadError(data.error || "Unknown error");
                    break;
                  }
                }
              } catch (parseError) {
                console.error("Failed to parse SSE event:", parseError);
              }

              // Reset for next event
              eventType = "";
              eventData = "";
            }
          }
        }
      } catch (err: any) {
        console.error("Upload failed:", err);
        setAnalysisPhase("error");
        setError(err.message);
        onUploadError(err.message);
      }
    },
    [
      documentTypeId,
      workspace_id,
      onStructureComplete,
      onPromptComplete,
      onPromptError,
      onAnalysisComplete,
      onUploadError,
      getAuthHeaders,
    ],
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: async (acceptedFiles) => {
      if (requireConfirmation) {
        setPendingUpload(acceptedFiles);
        setShowConfirmation(true);
        return;
      }

      setFiles(acceptedFiles);
      setError(null);

      for (const file of acceptedFiles) {
        await uploadFileWithStreaming(file);
      }
    },
    accept: {
      "application/pdf": [".pdf"],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
        [".docx"],
      "text/plain": [".txt"],
      "text/markdown": [".md"],
    },
    maxSize: 5 * 1024 * 1024, // 5MB
    multiple: false,
    disabled:
      analysisPhase !== "idle" &&
      analysisPhase !== "done" &&
      analysisPhase !== "error",
  });

  const handleConfirmation = useCallback(
    async (confirmed: boolean) => {
      setShowConfirmation(false);
      if (confirmed && pendingUpload.length > 0) {
        setFiles(pendingUpload);
        setError(null);

        for (const file of pendingUpload) {
          await uploadFileWithStreaming(file);
        }
      }
      setPendingUpload([]);
    },
    [pendingUpload, uploadFileWithStreaming],
  );

  const getFileIcon = (fileName: string) => {
    const ext = fileName.split(".").pop()?.toLowerCase();
    switch (ext) {
      case "pdf":
        return <PictureAsPdfIcon />;
      case "doc":
      case "docx":
      default:
        return <DescriptionIcon />;
    }
  };

  const getStatusMessage = () => {
    switch (analysisPhase) {
      case "uploading":
        return "Uploading file...";
      case "extracting":
        return "Extracting document structure...";
      case "generating":
        return `Generating prompts (${progress.completed}/${progress.total})...`;
      case "done":
        return "Analysis complete!";
      case "error":
        return "Analysis failed";
      default:
        return null;
    }
  };

  const isProcessing =
    analysisPhase === "uploading" ||
    analysisPhase === "extracting" ||
    analysisPhase === "generating";

  return (
    <>
      <Box sx={{ mb: 3 }}>
        <Box
          {...getRootProps()}
          sx={{
            border: "2px dashed",
            borderColor: isProcessing ? "primary.400" : "neutral.300",
            borderRadius: "sm",
            p: 3,
            textAlign: "center",
            cursor: isProcessing ? "default" : "pointer",
            transition: "all 0.2s ease",
            backgroundColor: isProcessing ? "primary.50" : "transparent",
            "&:hover": isProcessing
              ? {}
              : {
                  borderColor: "primary.500",
                  backgroundColor: "primary.50",
                },
          }}
        >
          <input {...getInputProps()} />
          {isProcessing ? (
            <Stack spacing={1} alignItems="center">
              <Typography level="body-sm" color="primary">
                {getStatusMessage()}
              </Typography>
              {analysisPhase === "generating" && progress.total > 0 && (
                <Box sx={{ width: "100%", maxWidth: 300 }}>
                  <Box
                    sx={{
                      height: 4,
                      backgroundColor: "neutral.200",
                      borderRadius: 2,
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        height: "100%",
                        width: `${(progress.completed / progress.total) * 100}%`,
                        backgroundColor: "primary.500",
                        transition: "width 0.3s ease",
                      }}
                    />
                  </Box>
                </Box>
              )}
            </Stack>
          ) : (
            <Typography level="body-sm" color="neutral">
              Upload template to analyze structure (.doc, .docx, .pdf)
            </Typography>
          )}
        </Box>

        {files.length > 0 && !isProcessing && (
          <List sx={{ mt: 2 }}>
            {files.map((file, index) => (
              <ListItem key={`new-${index}`}>
                <ListItemDecorator>{getFileIcon(file.name)}</ListItemDecorator>
                <ListItemContent>
                  <Typography level="body-sm">{file.name}</Typography>
                  <Typography level="body-xs">
                    {(file.size / 1024).toFixed(1)} KB
                  </Typography>
                </ListItemContent>
                {analysisPhase === "done" && (
                  <Chip size="sm" variant="soft" color="success">
                    Complete
                  </Chip>
                )}
              </ListItem>
            ))}
          </List>
        )}

        {error && (
          <Typography level="body-sm" color="danger" sx={{ mt: 1 }}>
            {error}
          </Typography>
        )}
      </Box>

      <Modal open={showConfirmation} onClose={() => handleConfirmation(false)}>
        <ModalDialog>
          <DialogTitle>{confirmationTitle}</DialogTitle>
          <Stack spacing={2}>
            <Typography>{confirmationMessage}</Typography>
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button
                variant="outlined"
                color="neutral"
                onClick={() => handleConfirmation(false)}
              >
                Cancel
              </Button>
              <Button
                variant="solid"
                color="primary"
                onClick={() => handleConfirmation(true)}
              >
                Continue
              </Button>
            </Stack>
          </Stack>
        </ModalDialog>
      </Modal>
    </>
  );
};

interface StructureTabProps {
  sections: DocumentTypeSection[];
  documentTypeId?: number;
  documentTypeName: string;
  dataFields: DocumentTypeInput[];
  availableSources: SourceDocumentDefinition[];
  workspace_id: string;
  onChange: (sections: DocumentTypeSection[]) => void;
  onChangeFunctional: (
    updater: (prev: DocumentTypeSection[]) => DocumentTypeSection[],
  ) => void;
  showValidation: boolean;
  navigationActions?: React.ReactNode;
}

const StructureTab: React.FC<StructureTabProps> = ({
  sections,
  documentTypeId,
  documentTypeName,
  dataFields,
  availableSources,
  workspace_id,
  onChange,
  onChangeFunctional,
  showValidation,
  navigationActions,
}) => {
  const { deleteSection } = useDeleteDocumentTypeSection(workspace_id);
  const { createSection } = useCreateDocumentTypeSection(workspace_id);
  const { updateSection: updateSectionAPI } =
    useUpdateDocumentTypeSection(workspace_id);
  const { getSections } = useDocumentTypeSections(workspace_id);
  const { setShowValidation } = useValidation();

  // Feature flags for gating Structure Agent and Thinking sections
  const isStructureAgentEnabled = useFeatureFlag("meridian_structure_agent");
  const isThinkingEnabled = useFeatureFlag("meridian_thinking_sections");
  const isSectionSourceSelectionEnabled = useFeatureFlag(
    "meridian_section_source_selection",
  );

  // Ref to store timeout IDs, keyed by index
  const debounceTimeouts = useRef<Record<number, NodeJS.Timeout>>({});

  // Replace File state with File ID state
  // const [selectedRefinementFile, setSelectedRefinementFile] = useState<File | null>(null);
  const [uploadedRefinementData, setUploadedRefinementData] =
    useState<UploadedRefinementData | null>(null);
  // Add state for the shared selected generated document ID
  const [selectedGeneratedDocId, setSelectedGeneratedDocId] = useState<
    number | ""
  >("");

  // Per-section prompt loading state for streaming template analysis
  const [promptLoadingState, setPromptLoadingState] = useState<{
    [sectionId: number]: "generating" | "complete" | "error";
  }>({});
  const [promptErrors, setPromptErrors] = useState<{
    [sectionId: number]: string;
  }>({});

  // Section pending delete confirmation (null = modal closed). The ID is kept
  // alongside the index so a list refresh while the modal is open (e.g. the
  // Structure Agent editing sections) can't retarget us at whatever slid into
  // that index. Unsaved sections have no ID yet, hence the index fallback.
  const [pendingDelete, setPendingDelete] = useState<{
    index: number;
    id: number | null;
  } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Which add button is currently creating a section ("header", or the index of
  // the section the user clicked below), or null when idle. Keeps the buttons
  // from firing twice while a create request is in flight.
  const [pendingAddSource, setPendingAddSource] = useState<
    number | "header" | null
  >(null);
  // Mirrors pendingAddSource. State updates are async, so two clicks in the
  // same tick would both see `null`; the ref closes that window.
  const addInFlight = useRef(false);

  // Clear timeouts when sections change
  useEffect(() => {
    Object.values(debounceTimeouts.current).forEach(clearTimeout);
    debounceTimeouts.current = {};
  }, [sections]);

  // Creates a section at a given position in the flat section list. Everything
  // below the insertion point shifts down one slot and has its OrderIndex
  // re-persisted, the same way drag-and-drop reordering does.
  const addSectionAt = useCallback(
    async (
      insertAt: number,
      parentSectionId: number | null,
      source: number | "header",
    ) => {
      if (!documentTypeId) return; // TODO: Detta som ledder till att Johan behövde klicka två ggr ? Gå igenom allt sen för att se ifall du prickat in allt.
      if (addInFlight.current) return; // An add is already in flight

      addInFlight.current = true;
      setShowValidation(false);
      setPendingAddSource(source);
      try {
        // Create section immediately on the backend
        const newSection = await createSection(documentTypeId, {
          Title: "", // Empty title - user will fill it in
          OrderIndex: insertAt,
          ParentSectionID: parentSectionId,
          Dynamic: false,
          prompts: [
            {
              Name: "Default",
              PromptText: "",
              IsDefault: true,
            },
          ],
        } as any);

        // Splice the persisted section into local state and renumber
        const reordered = [
          ...sections.slice(0, insertAt),
          newSection,
          ...sections.slice(insertAt),
        ].map((section, i) => ({ ...section, OrderIndex: i }));
        onChange(reordered);

        // Persist the shifted OrderIndex values so the new section keeps its
        // position after a reload. The backend orders each parent's children
        // independently, so only the new section's own siblings below it can
        // collide -- everything else keeps a valid order without a write.
        const shifted = reordered
          .slice(insertAt + 1)
          .filter(
            (section) =>
              section.DocumentTypeSectionID &&
              (section.ParentSectionID ?? null) === parentSectionId,
          );
        await Promise.all(
          shifted.map((section) =>
            updateSectionAPI(
              documentTypeId,
              section.DocumentTypeSectionID as number,
              { OrderIndex: section.OrderIndex } as any,
            ),
          ),
        );
      } catch (e) {
        console.error("Failed to create section:", e);
      } finally {
        addInFlight.current = false;
        setPendingAddSource(null);
      }
    },
    [
      sections,
      onChange,
      setShowValidation,
      documentTypeId,
      createSection,
      updateSectionAPI,
    ],
  );

  // Header button: append a top-level section at the end.
  const handleAddSection = useCallback(
    () => addSectionAt(sections.length, null, "header"),
    [addSectionAt, sections.length],
  );

  // Per-section button: insert a sibling right below the clicked section,
  // inheriting its parent, after any children it already has.
  const handleAddSectionBelow = useCallback(
    (index: number) => {
      const section = sections[index];
      if (!section) return;
      addSectionAt(
        getInsertIndexAfterSubtree(sections, index),
        section.ParentSectionID ?? null,
        index,
      );
    },
    [sections, addSectionAt],
  );

  const updateSection = useCallback(
    (index: number, updates: Partial<DocumentTypeSection>) => {
      const updatedSections = [...sections];
      const currentSection = updatedSections[index];

      // Reset validation when user starts typing
      if ("Title" in updates) {
        setShowValidation(false);
      }

      updatedSections[index] = {
        ...currentSection,
        ...updates,
        ...(currentSection.DocumentTypeSectionID && {
          DocumentTypeSectionID: currentSection.DocumentTypeSectionID,
        }),
      };

      onChange(updatedSections);
    },
    [sections, onChange, setShowValidation],
  );

  // Persist Title on blur: update only (section already exists)
  const persistTitle = useCallback(
    async (index: number) => {
      const section = sections[index];
      const trimmed = (section.Title || "").trim();
      if (!documentTypeId || !section.DocumentTypeSectionID) return;

      try {
        // Update existing section (all sections are now persisted on creation)
        await updateSectionAPI(documentTypeId, section.DocumentTypeSectionID, {
          Title: trimmed,
        } as any);
        // Update only the specific section locally using functional update
        onChangeFunctional((prevSections) =>
          prevSections.map((s, i) =>
            i === index ? { ...s, Title: trimmed } : s,
          ),
        );
      } catch (e) {
        console.error("Failed to persist title:", e);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onChange excluded, using onChangeFunctional instead
    [sections, documentTypeId, onChangeFunctional, updateSectionAPI],
  );

  // Persist Prompt on blur: update only (section already exists)
  const persistPrompt = useCallback(
    async (index: number) => {
      const section = sections[index];
      const promptText = section.prompts?.[0]?.PromptText ?? "";
      if (!documentTypeId || !section.DocumentTypeSectionID) return;
      // Skip if unchanged vs snapshot
      if (promptFocusSnapshot.current.get(index) === (promptText || "")) return;

      try {
        // Update existing section (all sections are now persisted on creation)
        await updateSectionAPI(documentTypeId, section.DocumentTypeSectionID, {
          prompts: [
            {
              Name: section.prompts?.[0]?.Name || "Default",
              PromptText: promptText,
            },
          ],
        } as any);
        onChangeFunctional((prevSections) =>
          prevSections.map((s, i) =>
            i === index
              ? {
                  ...s,
                  prompts: [
                    {
                      Name: s.prompts?.[0]?.Name || "Default",
                      PromptText: promptText,
                      IsDefault: true,
                    },
                  ],
                }
              : s,
          ),
        );
      } catch (e) {
        console.error("Failed to persist prompt:", e);
      }
    },
    [sections, documentTypeId, onChangeFunctional, updateSectionAPI],
  );

  // Snapshots for change detection on blur
  const titleFocusSnapshot = useRef<Map<number, string>>(new Map());
  const promptFocusSnapshot = useRef<Map<number, string>>(new Map());
  const onTitleFocus = useCallback((index: number, value: string) => {
    titleFocusSnapshot.current.set(index, (value || "").trim());
  }, []);
  const onPromptFocus = useCallback((index: number, value: string) => {
    promptFocusSnapshot.current.set(index, value || "");
  }, []);

  // Dynamic change handler
  const onDynamicChange = useCallback(
    async (index: number, checked: boolean) => {
      const section = sections[index];
      // Update UI immediately
      onChange(
        sections.map((s, i) => (i === index ? { ...s, Dynamic: checked } : s)),
      );
      if (!documentTypeId || !section.DocumentTypeSectionID) return;
      try {
        // Update existing section (all sections are now persisted on creation)
        await updateSectionAPI(documentTypeId, section.DocumentTypeSectionID, {
          Dynamic: checked,
        } as any);
        // Local state already updated above, no need to update again
      } catch (e) {
        console.error("Failed to update Dynamic:", e);
      }
    },
    [sections, documentTypeId, onChange, updateSectionAPI],
  );

  const onSectionTypeChange = useCallback(
    async (index: number, sectionType: "Standard" | "Thinking") => {
      const section = sections[index];
      // Update UI immediately
      onChange(
        sections.map((s, i) =>
          i === index ? { ...s, SectionType: sectionType } : s,
        ),
      );
      if (!documentTypeId || !section.DocumentTypeSectionID) return;
      try {
        // Update existing section
        await updateSectionAPI(documentTypeId, section.DocumentTypeSectionID, {
          SectionType: sectionType,
        } as any);
      } catch (e) {
        console.error("Failed to update SectionType:", e);
      }
    },
    [sections, documentTypeId, onChange, updateSectionAPI],
  );

  const onSourcesChange = useCallback(
    async (index: number, sourceFieldIds: number[]) => {
      const section = sections[index];
      // Update UI immediately
      onChange(
        sections.map((s, i) =>
          i === index ? { ...s, AllowedSourceFieldIDs: sourceFieldIds } : s,
        ),
      );
      if (!documentTypeId || !section.DocumentTypeSectionID) return;
      try {
        await updateSectionAPI(documentTypeId, section.DocumentTypeSectionID, {
          AllowedSourceFieldIDs: sourceFieldIds,
        } as any);
      } catch (e) {
        console.error("Failed to update section sources:", e);
      }
    },
    [sections, documentTypeId, onChange, updateSectionAPI],
  );

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      Object.values(debounceTimeouts.current).forEach(clearTimeout);
    };
  }, []);

  const removeSection = useCallback(
    async (index: number) => {
      const sectionToRemove = sections[index];

      try {
        if (sectionToRemove.DocumentTypeSectionID && documentTypeId) {
          await deleteSection(
            documentTypeId,
            sectionToRemove.DocumentTypeSectionID,
          );
          const refreshed = await getSections(documentTypeId);
          onChange(refreshed);
          return;
        }
        const updatedSections = sections.filter((_, i) => i !== index);
        onChange(updatedSections);
      } catch (error) {
        console.error("Failed to delete section:", error);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getSections is stable
    [sections, documentTypeId, deleteSection, onChange],
  );

  // Deleting a section also deletes everything nested underneath it. That
  // hidden extra loss is the whole reason for the confirmation modal, so a
  // section with no children is deleted straight away.
  const countDescendants = useCallback(
    (index: number): number => {
      const rootId = sections[index]?.DocumentTypeSectionID;
      if (!rootId) return 0;

      let count = 0;
      let frontier = [rootId];
      while (frontier.length > 0) {
        const children = sections.filter(
          (s) =>
            s.ParentSectionID != null && frontier.includes(s.ParentSectionID),
        );
        count += children.length;
        frontier = children
          .map((s) => s.DocumentTypeSectionID)
          .filter((id): id is number => typeof id === "number");
      }
      return count;
    },
    [sections],
  );

  const requestRemoveSection = useCallback(
    (index: number) => {
      // Only a section with children takes something extra down with it, so
      // that's the only case worth interrupting the user for.
      if (countDescendants(index) === 0) {
        removeSection(index);
        return;
      }
      setPendingDelete({
        index,
        id: sections[index]?.DocumentTypeSectionID ?? null,
      });
    },
    [sections, countDescendants, removeSection],
  );

  // Current index of the section the modal is about, re-resolved by ID so the
  // modal text and the actual deletion always refer to the same section.
  const pendingDeleteIndex = useMemo(() => {
    if (!pendingDelete) return null;
    if (pendingDelete.id === null) return pendingDelete.index;
    const index = sections.findIndex(
      (s) => s.DocumentTypeSectionID === pendingDelete.id,
    );
    return index === -1 ? null : index;
  }, [pendingDelete, sections]);

  const cancelRemoveSection = useCallback(() => {
    if (isDeleting) return;
    setPendingDelete(null);
  }, [isDeleting]);

  const confirmRemoveSection = useCallback(async () => {
    if (pendingDeleteIndex === null) {
      setPendingDelete(null);
      return;
    }

    setIsDeleting(true);
    try {
      await removeSection(pendingDeleteIndex);
    } finally {
      setIsDeleting(false);
      setPendingDelete(null);
    }
  }, [pendingDeleteIndex, removeSection]);

  const onDragEnd = useCallback(
    async (result: DropResult) => {
      if (!result.destination) return;
      if (result.source.index === result.destination.index) return;

      const items = Array.from(sections);
      const [reorderedItem] = items.splice(result.source.index, 1);
      items.splice(result.destination.index, 0, reorderedItem);

      // Update orderIndex values for all items
      const updatedSections = items.map((section, index) => ({
        ...section,
        OrderIndex: index,
      }));

      // Update local state immediately
      onChange(updatedSections);

      var affectedSections;
      if (result.destination.index > result.source.index) {
        affectedSections = updatedSections.slice(
          result.source.index,
          result.destination.index + 1,
        );
      } else {
        affectedSections = updatedSections.slice(
          result.destination.index,
          result.source.index + 1,
        );
      }

      // Persist OrderIndex changes to database for all affected sections
      if (documentTypeId) {
        try {
          const updatePromises = affectedSections.map(async (section) => {
            return updateSectionAPI(
              documentTypeId,
              section.DocumentTypeSectionID!,
              { OrderIndex: section.OrderIndex } as any,
            );
          });

          await Promise.all(updatePromises);
        } catch (e) {
          console.error("Failed to persist OrderIndex changes:", e);
        }
      }
    },
    [sections, onChange, documentTypeId, updateSectionAPI],
  );

  // Handler for structure_complete SSE event - Phase 1 done
  const handleStructureComplete = useCallback(
    (newSections: DocumentTypeSection[]) => {
      // Initialize all sections as "generating" state
      const loadingState: { [sectionId: number]: "generating" } = {};
      newSections.forEach((section) => {
        if (section.DocumentTypeSectionID) {
          loadingState[section.DocumentTypeSectionID] = "generating";
        }
      });
      setPromptLoadingState(loadingState);
      setPromptErrors({});
      onChange(newSections);
    },
    [onChange],
  );

  // Handler for prompt_complete SSE event - individual prompt ready
  const handlePromptComplete = useCallback(
    (sectionId: number, promptText: string) => {
      // Update the prompt in local state
      onChangeFunctional((prevSections) =>
        prevSections.map((s) =>
          s.DocumentTypeSectionID === sectionId
            ? {
                ...s,
                prompts: [
                  {
                    Name: "Default",
                    PromptText: promptText,
                    IsDefault: true,
                  },
                ],
              }
            : s,
        ),
      );
      // Mark section as complete
      setPromptLoadingState((prev) => ({
        ...prev,
        [sectionId]: "complete",
      }));
    },
    [onChangeFunctional],
  );

  // Handler for prompt_error SSE event - prompt generation failed
  const handlePromptError = useCallback((sectionId: number, error: string) => {
    setPromptLoadingState((prev) => ({
      ...prev,
      [sectionId]: "error",
    }));
    setPromptErrors((prev) => ({
      ...prev,
      [sectionId]: error,
    }));
  }, []);

  // Handler for done SSE event - all processing complete
  const handleAnalysisComplete = useCallback(() => {
    // Clear any remaining "generating" states to "complete"
    setPromptLoadingState((prev) => {
      const updated = { ...prev };
      for (const key in updated) {
        if (updated[key] === "generating") {
          updated[key] = "complete";
        }
      }
      return updated;
    });
  }, []);

  // Add a constant for maximum depth if needed
  const MAX_DEPTH = 5; // Or any other reasonable limit, or remove for infinite depth

  // Measured at click time by the StructureNavigator so scroll targets clear
  // the sticky toolbar, whose height changes with the breakpoint.
  const stickyHeaderRef = useRef<HTMLDivElement>(null);

  const navNodes = useMemo(
    () => buildTreeFromDocumentTypeSections(sections),
    [sections],
  );

  // Focus the title input of the section we just scrolled to. preventScroll
  // stops the browser re-scrolling and fighting our own smooth scroll.
  const focusSectionTitle = useCallback((id: string) => {
    document
      .querySelector<HTMLInputElement>(
        `[data-nav-section-id="${id}"] [data-section-title-input]`,
      )
      ?.focus({ preventScroll: true });
  }, []);

  const getIndentLevel = useCallback(
    (section: DocumentTypeSection): number => {
      const parentMap = new Map(
        sections.map((s) => [s.DocumentTypeSectionID, s.ParentSectionID]),
      );
      let level = 0;
      let currentParentId: number | null | undefined = section.ParentSectionID;
      while (currentParentId && level < 10) {
        level++;
        // Explicitly handle undefined case from map lookup
        currentParentId = parentMap.get(currentParentId);
        if (currentParentId === undefined) {
          // Check for undefined explicitly
          currentParentId = null; // Treat undefined as null (end of chain)
        }
      }
      return level;
    },
    [sections],
  );

  // Memoize the map of available parents for each section
  const availableParentsMap = useMemo(() => {
    const parentMap = new Map<number | undefined, DocumentTypeSection[]>();

    // Helper functions (can be defined here or outside if preferred)
    const isDescendant = (
      sectionId: number | null | undefined,
      targetId: number | null | undefined,
      allSections: DocumentTypeSection[],
    ): boolean => {
      if (!sectionId) return false;
      const section = allSections.find(
        (s) => s.DocumentTypeSectionID === sectionId,
      );
      if (!section) return false;
      if (section.ParentSectionID === targetId) return true;
      return isDescendant(section.ParentSectionID, targetId, allSections);
    };
    const calculateDepth = (
      sectionId: number | null | undefined,
      allSections: DocumentTypeSection[],
    ): number => {
      let depth = 0;
      let currentParentId = sectionId;
      const idToParentId = new Map(
        allSections.map((s) => [s.DocumentTypeSectionID, s.ParentSectionID]),
      );
      const visited = new Set();
      while (currentParentId && depth < 10) {
        // Limit depth
        if (visited.has(currentParentId)) return 10; // Cycle detected
        visited.add(currentParentId);
        const parentId = idToParentId.get(currentParentId);
        if (!parentId) break; // Reached root
        depth++;
        currentParentId = parentId;
      }
      return depth;
    };

    // Calculate available parents for each section
    sections.forEach((currentSection) => {
      const currentId = currentSection.DocumentTypeSectionID;
      const available = sections.filter((potentialParent) => {
        const potentialParentId = potentialParent.DocumentTypeSectionID;
        // Cannot be its own parent
        if (potentialParentId === currentId) return false;
        // Cannot create a cycle
        if (isDescendant(potentialParentId, currentId, sections)) return false;
        // Check max depth if adding as parent
        if (calculateDepth(potentialParentId, sections) >= MAX_DEPTH)
          return false;
        return true;
      });
      parentMap.set(currentId, available);
    });

    return parentMap;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sectionStructureDependency is derived from sections
  }, [sections]);

  const updateSectionParent = useCallback(
    (index: number, parentId: number | null) => {
      const updatedSections = [...sections];
      updatedSections[index] = {
        ...updatedSections[index],
        ParentSectionID: parentId,
      };
      onChange(updatedSections);
      if (documentTypeId && updatedSections[index].DocumentTypeSectionID) {
        updateSectionAPI(
          documentTypeId,
          updatedSections[index].DocumentTypeSectionID as number,
          { ParentSectionID: parentId } as any,
        ).catch((e) => console.error("Failed to update parent:", e));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- documentTypeId accessed in callback
    [sections, onChange, updateSectionAPI],
  );

  return (
    <>
      {/* The sticky heading row spans the full width; the two-column split
          (editor + outline rail) starts beneath it. The rail is a real column
          rather than an overlay, so it can never cover the section cards. */}
      <Stack spacing={3}>
        <Stack
          ref={stickyHeaderRef}
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", md: "center" }}
          spacing={1.5}
          sx={{
            position: "sticky",
            // Pin BELOW the fixed app shell upperbar so the sticky header
            // doesn't slide under it. UPPERBAR_HEIGHT_PX is the single
            // source of truth for the upperbar height (NavContainer +
            // NavContainerNoSidebar both consume the same constant).
            top: `${UPPERBAR_HEIGHT_PX}px`,
            zIndex: 20,
            bgcolor: "background.surface",
            py: 1.5,
            borderBottom: "1px solid",
            borderColor: "divider",
            boxShadow: "sm",
          }}
        >
          <Typography level="h3">Document Structure</Typography>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            sx={{
              alignItems: { xs: "stretch", sm: "center" },
              flexWrap: "wrap",
              justifyContent: { xs: "stretch", sm: "flex-end" },
            }}
          >
            <AddSectionButton
              onClick={handleAddSection}
              tooltip="Add a section at the end of the document"
              loading={pendingAddSource === "header"}
              disabled={
                pendingAddSource !== null && pendingAddSource !== "header"
              }
            />
            {navigationActions}
          </Stack>
        </Stack>

        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={{ xs: 0, md: 3 }}
          alignItems="flex-start"
        >
          <Stack spacing={3} sx={{ flex: 1, minWidth: 0, width: "100%" }}>
            {documentTypeId && (
              <Box>
                <Typography level="h3" sx={{ mb: 2 }}>
                  Template
                </Typography>
                <JoyFileUploader
                  documentTypeId={documentTypeId}
                  workspace_id={workspace_id}
                  onStructureComplete={handleStructureComplete}
                  onPromptComplete={handlePromptComplete}
                  onPromptError={handlePromptError}
                  onAnalysisComplete={handleAnalysisComplete}
                  onUploadError={(error: string) =>
                    console.error("Template analysis failed:", error)
                  }
                  requireConfirmation={sections.length > 0}
                  confirmationMessage="This will delete all existing sections and their prompts. Are you sure you want to continue?"
                  confirmationTitle="Confirm Template Analysis"
                />
              </Box>
            )}

            <Stack spacing={2}>
              <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="sections">
                  {(provided, snapshot) => (
                    <Box {...provided.droppableProps} ref={provided.innerRef}>
                      {sections.map((section, index) => {
                        const sectionIdKey =
                          section.DocumentTypeSectionID || `new-${index}`;
                        // Get pre-calculated parents for this specific section
                        const availableParents =
                          availableParentsMap.get(
                            section.DocumentTypeSectionID,
                          ) || [];
                        return (
                          <Draggable
                            key={sectionIdKey}
                            draggableId={sectionIdKey.toString()}
                            index={index}
                          >
                            {(providedDraggable, snapshotDraggable) => (
                              <SectionItem
                                key={sectionIdKey}
                                section={section}
                                index={index}
                                sectionIdKey={sectionIdKey.toString()}
                                provided={providedDraggable}
                                snapshot={snapshotDraggable}
                                updateSection={updateSection}
                                requestRemoveSection={requestRemoveSection}
                                getIndentLevel={getIndentLevel}
                                documentTypeId={documentTypeId}
                                sections={sections}
                                availableParents={availableParents}
                                updateSectionParent={updateSectionParent}
                                uploadedRefinementData={uploadedRefinementData}
                                setUploadedRefinementData={
                                  setUploadedRefinementData
                                }
                                selectedGeneratedDocId={selectedGeneratedDocId}
                                setSelectedGeneratedDocId={
                                  setSelectedGeneratedDocId
                                }
                                persistTitle={persistTitle}
                                persistPrompt={persistPrompt}
                                onTitleFocus={onTitleFocus}
                                onPromptFocus={onPromptFocus}
                                onDynamicChange={onDynamicChange}
                                onSectionTypeChange={onSectionTypeChange}
                                onSourcesChange={onSourcesChange}
                                availableSources={availableSources}
                                isSectionSourceSelectionEnabled={
                                  isSectionSourceSelectionEnabled
                                }
                                isThinkingEnabled={isThinkingEnabled}
                                showValidation={showValidation}
                                promptLoadingState={
                                  section.DocumentTypeSectionID
                                    ? promptLoadingState[
                                        section.DocumentTypeSectionID
                                      ]
                                    : undefined
                                }
                                promptError={
                                  section.DocumentTypeSectionID
                                    ? promptErrors[
                                        section.DocumentTypeSectionID
                                      ]
                                    : undefined
                                }
                                onAddSectionBelow={handleAddSectionBelow}
                                isAddingHere={pendingAddSource === index}
                                isAddPending={pendingAddSource !== null}
                              />
                            )}
                          </Draggable>
                        );
                      })}
                      {provided.placeholder}
                    </Box>
                  )}
                </Droppable>
              </DragDropContext>
            </Stack>
          </Stack>

          {/* Outline rail */}
          <StructureNavigator
            nodes={navNodes}
            storageKey="meridian_structure_nav_hidden"
            scrollOffsetRef={stickyHeaderRef}
            onNavigate={focusSectionTitle}
          />
        </Stack>
      </Stack>

      {/* Delete confirmation for the per-section trash button */}
      <Modal open={pendingDeleteIndex !== null} onClose={cancelRemoveSection}>
        <ModalDialog variant="outlined" role="alertdialog">
          <DialogTitle>Delete section?</DialogTitle>
          <Stack spacing={2}>
            {pendingDeleteIndex !== null &&
              (() => {
                const section = sections[pendingDeleteIndex];
                const title = (section?.Title || "").trim();
                const childCount = countDescendants(pendingDeleteIndex);
                return (
                  <Stack spacing={1}>
                    <Typography>
                      Are you sure you want to delete{" "}
                      <Typography fontWeight="lg">
                        {title || "this untitled section"}
                      </Typography>
                      ?
                    </Typography>
                    <Typography level="body-sm" color="danger">
                      This also deletes {childCount} nested section
                      {childCount === 1 ? "" : "s"} underneath it.
                    </Typography>
                    <Typography level="body-sm" sx={{ color: "text.tertiary" }}>
                      The sections and their prompts are removed permanently.
                      This cannot be undone.
                    </Typography>
                  </Stack>
                );
              })()}
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button
                variant="outlined"
                color="neutral"
                onClick={cancelRemoveSection}
                disabled={isDeleting}
              >
                Cancel
              </Button>
              <Button
                variant="solid"
                color="danger"
                onClick={confirmRemoveSection}
                loading={isDeleting}
              >
                Delete
              </Button>
            </Stack>
          </Stack>
        </ModalDialog>
      </Modal>

      {/* Structure Agent Chat Widget - gated by feature flag */}
      {isStructureAgentEnabled && documentTypeId && (
        <FloatingChatWidget
          documentTypeId={documentTypeId}
          documentTypeName={documentTypeName}
          sections={sections}
          dataFields={dataFields}
          workspaceId={workspace_id}
          onSectionsChanged={() => {
            getSections(documentTypeId).then(onChange);
          }}
        />
      )}
    </>
  );
};

export default StructureTab;
